import React, { useEffect, useRef, useState } from 'react';
import { Card, Image, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './rightsidebar.css';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useConnections } from '../../../../Context/ConnectionContext';
import { getToken } from '../../../../utils/auth';


const RightSidebar = ({ isRTL }) => {
  const navigate = useNavigate();
  const [connection, setConnection] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [newMessage, setNewMessage] = useState(''); 
  const { t, i18n } = useTranslation();
  const { connections, handleConnections, loading, error } = useConnections();
  const chatBoxRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const minirightsidebar = () => {
    const sidebar = document.getElementById('rightSidebar');
    const isCurrentlyOpen = sidebar.classList.contains('right-sidebar');
    
    sidebar.classList.toggle('right-sidebar');
    document.body.classList.toggle('right-sidebar-close');
    
    setIsSidebarOpen(!isCurrentlyOpen);
  };

  // Function to force sidebar closed on mobile
  const ensureSidebarClosedOnMobile = () => {
    const isMobile = window.innerWidth <= 768; // Adjust breakpoint as needed
    const sidebar = document.getElementById('rightSidebar');
    
    if (isMobile && sidebar) {
      // Force remove the class that opens the sidebar
      sidebar.classList.remove('right-sidebar');
      document.body.classList.remove('right-sidebar-close');
      setIsSidebarOpen(false);
    }
  };

  const handleNavigateProfile = (userid) => {
    navigate(`/userprofile/${userid}`);
  };

  const [user, setUser] = useState("");
  const fetchUserData = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/user`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUser(response?.data?.data._id);
    } catch (error) {
      console.error("Failed Fetching Details", error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [])

  const [unread, setUnread] = useState(0);
  const handleConnection = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${process.env.REACT_APP_URL}/api/connection/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setConnection(response.data.data.connectionList);
      setUnread(response.data.data.totalUnreadMessages)
    } catch (error) {
      console.log('Failed Fetching Connections', error);
    }
  };

  const fetchChatHistory = async (userId) => {
    try {
      const token = getToken();
      const response = await axios.get(`${process.env.REACT_APP_URL}/api/chat/all/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setChatHistory(response.data.data);
    } catch (error) {
      console.log('Failed Fetching Chat History', error);
    }
  };

  const handleUserClick = (userDetails) => {
    setSelectedUser(userDetails);
    fetchChatHistory(userDetails._id);
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() && selectedUser) {
      try {
        const token = getToken();
        await axios.post(
          `${process.env.REACT_APP_URL}/api/chat/send`,
          {
            receiver: selectedUser._id,
            text: newMessage,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        fetchChatHistory(selectedUser._id);
        setNewMessage('');
      } catch (error) {
        console.log('Failed Sending Message', error);
      }
    }
  };

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleBackToConnections = () => {
    setSelectedUser(null);
    setConnection([])
    setUnread(0);
    handleConnections();
  };

  useEffect(() => {
    handleConnections();
    const intervalId = setInterval(() => {
      handleConnections();
    }, 120000);
    return () => clearInterval(intervalId);
  }, []);

  // Initialize sidebar as CLOSED on mount - runs first
  useEffect(() => {
    const sidebar = document.getElementById('rightSidebar');
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile && sidebar) {
      // Forcefully close sidebar on initial render
      sidebar.classList.remove('right-sidebar');
      document.body.classList.remove('right-sidebar-close');
      setIsSidebarOpen(false);
    }
  }, []); // Empty dependency - runs only once on mount

  // Handle language/direction changes
  useEffect(() => {
    // Multiple checks to ensure sidebar stays closed during RTL switch
    ensureSidebarClosedOnMobile();
    
    const timer1 = setTimeout(() => ensureSidebarClosedOnMobile(), 50);
    const timer2 = setTimeout(() => ensureSidebarClosedOnMobile(), 150);
    const timer3 = setTimeout(() => ensureSidebarClosedOnMobile(), 300);
    const timer4 = setTimeout(() => ensureSidebarClosedOnMobile(), 500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [i18n.language]);

  // Watch for dir attribute changes (when Default component changes dir)
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'dir') {
          // Dir changed (RTL switch), force close on mobile
          ensureSidebarClosedOnMobile();
          setTimeout(() => ensureSidebarClosedOnMobile(), 100);
          setTimeout(() => ensureSidebarClosedOnMobile(), 300);
          setTimeout(() => ensureSidebarClosedOnMobile(), 500);
        }
      });
    });

    // Observe the parent div or document
    const targetNode = document.querySelector('[dir]') || document.documentElement;
    observer.observe(targetNode, {
      attributes: true,
      attributeFilter: ['dir']
    });

    return () => observer.disconnect();
  }, []);

  return (<>
    <div dir={isRTL ? "rtl" : "ltr"} className="right-sidebar-mini" id="rightSidebar" style={{zIndex:"999"}}>
      <div className="right-sidebar-panel p-0">
        <Card className="shadow-none">
          <Card.Body className="p-0">
            {selectedUser ? (
              <div className="chat-container" style={{marginTop:"20px"}}>
                <div className="chat-header d-flex  align-items-center p-3">
                  <Button variant="link" onClick={handleBackToConnections} className="d-flex align-items-center">
                    <i className="fas fa-arrow-left"></i>
                  </Button>
                  <h6>{t('chat_with')} {selectedUser.firstName}</h6>
                </div>

                <div className="chat-box flex-grow-1" ref={chatBoxRef} style={{ overflowY: 'auto', paddingBlock: '10px' }}>
                  {chatHistory?.map((chat, index) => {
                    const currentMessageDate = moment(chat.createdAt).format('YYYY-MM-DD');
                    const previousMessageDate = index > 0 ? moment(chatHistory[index - 1].createdAt).format('YYYY-MM-DD') : null;
                    const isCurrentUser = chat.sender === user;

                    return (
                      <div key={index}>
                        {currentMessageDate !== previousMessageDate && (
                          <div className="text-center text-muted" style={{ marginBottom: '10px' }}>
                            {moment(chat.createdAt).format('MMMM DD, YYYY')}
                          </div>
                        )}

                        <div className={`d-flex ${isCurrentUser ? 'justify-content-end' : 'justify-content-start'}`}>
                          <div
                            style={{
                              position: 'relative',
                              textAlign: isCurrentUser ? 'right' : 'left',
                              marginLeft: isCurrentUser ? 'auto' : '10px',
                              marginRight: isCurrentUser ? '10px' : 'auto',
                              marginTop: '10px',
                              padding: '0.75rem 1rem',
                              clear: 'both',
                              wordBreak: 'break-word',
                              color: '#fff',
                              background: 'var(--bs-primary)',
                              borderRadius: '0.938rem',
                            }}
                          >
                            <div><strong className="text-muted">{isCurrentUser ? 'You' : `${selectedUser.firstName}`}</strong></div>
                            <div>{chat.text}</div>
                            <small className="text-muted" style={{ display: 'flex', justifyContent: 'right'}}>
                              {moment(chat.createdAt).format('HH:mm')}
                            </small>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Form className="d-flex p-3 chat-input-container">
                  <Form.Control
                    type="text"
                    placeholder={t('type_a_message')}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                  />
                  <Button variant="primary" onClick={handleSendMessage} style={{ marginLeft: '10px' }}>
                    {t('send')}
                  </Button>
                </Form>
              </div>
            ) : (
              <div className="connection-list connectionbox">
                <div className="filter-icon" style={{ display: 'flex', alignItems: 'center', padding: '10px', paddingBottom:"0px" }}>
                  <div style={{ fontSize: '20px', flexWrap:"nowrap" }}>{t('my_connections')}</div>
                </div>
                <div className="filter-icon" style={{ display: 'flex', alignItems: 'center', padding: '10px', paddingTop:"0px" }}>
                  {unread > 0 && (
                    <span className="unread-badge" style={{ color: '#50b5ff', fontSize: '15px' }}>
                      {unread} {t('unread')}
                    </span>
                  )}
                </div>

                <div className="media-height p-3" data-scrollbar="init">
                  {connections?.length === 0 ? (
                    <Card className="card-block card-stretch card-height">
                      <Card.Body className="profile-page p-0">
                        <div className="profile-header-image">
                          <div
                            className="cover-container"
                            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
                          >
                            <img
                              loading="lazy"
                              src="/noconnection.png"
                              alt="profile-bg"
                              style={{ width: '100%', height: 'auto' }}
                            />
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ) : (
                    connections?.map((data, index) => {
                      const userDetails = data?.userId || data?.connectionUserId;
                      return (
                        userDetails && (
                          <div className="d-flex align-items-center mb-4" key={index}>
                            <div className="">
                              <Image onClick={() => handleNavigateProfile(userDetails?.UID)}
                                className="rounded-circle avatar-50"
                                src={userDetails?.profileImageLink || '/profile.png'}
                                alt=""
                                loading="lazy"
                                style={{cursor:"pointer"}}
                              />
                            </div>
                            <div className="ms-3">
                              <h6
                                className="mb-0"
                                style={{ textTransform: 'capitalize', cursor: 'pointer' }}
                                onClick={() => handleUserClick(userDetails)}
                              >
                                {userDetails?.firstName} {userDetails?.lastName}
                              </h6>
                              {data?.unreadMessageCount > 0 && (
                                <span className="unread-badge" style={{ color: '#50b5ff', fontSize: '12px' }}>
                                  {data?.unreadMessageCount} {t('unread')}
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      );
                    })
                  )}
                </div>
              </div>
            )}
            {/* <div className="right-sidebar-toggle bg-primary text-white mt-5 d-flex" style={{paddingRight:"10px", marginTop:"40px"}} dir="ltr"  onClick={minirightsidebar}>
              <span className="material-symbols-outlined" >chat</span>
              {unread > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-1px',
                    right:'5px',
                    backgroundColor: 'red',
                    color: 'white',
                    borderRadius: '50%',
                    padding: '2px 2px',
                    fontSize: '10px', 
                    minWidth: '16px',
                    height: '16px', 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {unread}
                </span>
              )}
            </div> */}
          </Card.Body>
        </Card>
      </div>
    </div>
<div style={{ position: "fixed", top: 0, right: 0, height: "100%", zIndex: 9999 }}>
  
  {/* CHAT TOGGLE BUTTON — moves with sidebar */}
  <div 
    className="right-sidebar-toggle bg-primary text-white d-flex"
    style={{
      padding: "5px",
      position: "absolute",
      right: isSidebarOpen ? "240px" : "0px", // ⭐ moves with sidebar
      top: "50%",
      transform: "translateY(-50%)",
      borderRadius: "50px 0 0 50px",
      transition: "right 0.3s ease"
    }}
    dir="ltr"
    onClick={minirightsidebar}
  >
    <span className="material-symbols-outlined">chat</span>

    {unread > 0 && (
      <span style={{
        position: 'absolute',
        top: '-3px',
        left: '5px',
        backgroundColor: 'red',
        color: 'white',
        borderRadius: '50%',
        padding: '1px 2px',
        fontSize: '10px',
        minWidth: '16px',
        height: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        {unread}
      </span>
    )}
  </div>

  {/* RIGHT SIDEBAR */}
  <div
    id="rightSidebar"
    className={`right-sidebar-mini ${isSidebarOpen ? "open" : ""}`}
    style={{
      width: "300px",
      position: "absolute",
      right: 0,
      top: 0,
      height: "100%",
      transition: "transform 0.3s ease",
      transform: isSidebarOpen ? "translateX(0)" : "translateX(100%)"
    }}
  >
    {/* sidebar content */}
  </div>

</div>

    </>
  );
};

export default RightSidebar;